// src/pages/UFSValidationResults.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyRequests } from '../services/api';

const STATUS_BADGES = {
    APPROVED: { label: 'Approved', color: '#0d6832', bg: '#d4edda', icon: '✅' },
    DENIED: { label: 'Denied', color: '#721c24', bg: '#f8d7da', icon: '❌' },
    PENDING: { label: 'Pending', color: '#856404', bg: '#fff3cd', icon: '⏳' },
    PENDING_APPROVAL: { label: 'Pending Approval', color: '#856404', bg: '#fff3cd', icon: '⏳' },
    CANCELLED: { label: 'Cancelled', color: '#383d41', bg: '#e2e3e5', icon: '🚫' },
    EXPIRED: { label: 'Expired', color: '#383d41', bg: '#e2e3e5', icon: '⏰' },
    ON_HOLD: { label: 'On Hold', color: '#004085', bg: '#cce5ff', icon: '⏸️' },
    APPROVAL_FAILED: { label: 'Failed', color: '#721c24', bg: '#f8d7da', icon: '⚠️' },
};

const RECOMMENDATION_BADGES = {
    FULL_APPROVAL: { label: 'Full Approval', color: '#0d6832', bg: '#d4edda' },
    PARTIAL_APPROVAL: { label: 'Partial', color: '#856404', bg: '#fff3cd' },
    REJECT: { label: 'Reject', color: '#721c24', bg: '#f8d7da' },
};

const UFSValidationResults = () => {
    const [requestId, setRequestId] = useState('');
    const [userAlias, setUserAlias] = useState(() => {
        return localStorage.getItem('ufs_user_alias') || '';
    });
    const [aliasInput, setAliasInput] = useState(() => {
        return localStorage.getItem('ufs_user_alias') || '';
    });
    const [myRequests, setMyRequests] = useState([]);
    const [loadingRequests, setLoadingRequests] = useState(false);
    const [requestsError, setRequestsError] = useState(null);
    const [filterStatus, setFilterStatus] = useState('ALL');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('date_desc');
    const [recentSearches, setRecentSearches] = useState(() => {
        try {
            const saved = localStorage.getItem('ufs_recent_searches');
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });
    const navigate = useNavigate();

    // Fetch user's requests
    const fetchMyRequests = useCallback(async (alias) => {
        if (!alias) return;

        setLoadingRequests(true);
        setRequestsError(null);

        try {
            const result = await getMyRequests(alias);
            setMyRequests(result.requests || []);
        } catch (err) {
            console.error('Error fetching requests:', err);
            // Retry once after 2 seconds if unauthorized (token may not be ready yet)
            if (err.message && (err.message.includes('Unauthorized') || err.message.includes('401'))) {
                setTimeout(async () => {
                    try {
                        const retryResult = await getMyRequests(alias);
                        setMyRequests(retryResult.requests || []);
                        setRequestsError(null);
                    } catch (retryErr) {
                        setRequestsError(retryErr.message);
                        setMyRequests([]);
                    } finally {
                        setLoadingRequests(false);
                    }
                }, 2000);
                return;
            }
            setRequestsError(err.message);
            setMyRequests([]);
        } finally {
            setLoadingRequests(false);
        }
    }, []);

    // Auto-fetch on load if alias exists
    useEffect(() => {
        if (userAlias) {
            fetchMyRequests(userAlias);
        }
    }, [userAlias, fetchMyRequests]);

    // Handle alias submit
    const handleAliasSubmit = (e) => {
        e.preventDefault();
        const trimmed = aliasInput.trim().toLowerCase();
        if (!trimmed) return;

        setUserAlias(trimmed);
        localStorage.setItem('ufs_user_alias', trimmed);
        fetchMyRequests(trimmed);
    };

    // Handle alias change
    const handleChangeAlias = () => {
        setUserAlias('');
        setMyRequests([]);
        setRequestsError(null);
    };

    // Handle manual request ID lookup
    const handleViewResults = (e) => {
        e.preventDefault();
        const trimmedId = requestId.trim();

        if (!trimmedId) {
            alert('Please enter a Request ID');
            return;
        }

        const updatedSearches = [
            { id: trimmedId, timestamp: new Date().toISOString() },
            ...recentSearches.filter(s => s.id !== trimmedId)
        ].slice(0, 10);

        setRecentSearches(updatedSearches);
        localStorage.setItem('ufs_recent_searches', JSON.stringify(updatedSearches));

        navigate(`/viewer?id=${encodeURIComponent(trimmedId)}`);
    };

    const handleQuickView = (id) => {
        window.open(`/viewer?id=${encodeURIComponent(id)}`, '_blank');
    };

    const handleClearHistory = () => {
        setRecentSearches([]);
        localStorage.removeItem('ufs_recent_searches');
    };

    const formatDate = (isoString) => {
        try {
            const date = new Date(isoString);
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return 'Unknown';
        }
    };

    // Filter requests
    const filteredRequests = myRequests
        .filter(req => {
            if (filterStatus !== 'ALL') {
                const status = req.approvalStatus || req.status || 'PENDING';
                if (status !== filterStatus) return false;
            }
            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                return (
                    (req.requestId || '').toLowerCase().includes(q) ||
                    (req.teamLabel || '').toLowerCase().includes(q) ||
                    (req.orgName || '').toLowerCase().includes(q) ||
                    (req.functionDropdown || '').toLowerCase().includes(q) ||
                    (req.useCase || '').toLowerCase().includes(q) ||
                    (req.approvalId || '').toString().toLowerCase().includes(q)
                );
            }
            return true;
        })
        .sort((a, b) => {
            if (sortBy === 'date_desc') return new Date(b.createdAt) - new Date(a.createdAt);
            if (sortBy === 'date_asc') return new Date(a.createdAt) - new Date(b.createdAt);
            if (sortBy === 'status') {
                const statusOrder = { APPROVED: 0, PENDING_APPROVAL: 1, PENDING: 2, DENIED: 3, APPROVAL_FAILED: 4, CANCELLED: 5 };
                const sa = statusOrder[a.approvalStatus || a.status || 'PENDING'] ?? 9;
                const sb = statusOrder[b.approvalStatus || b.status || 'PENDING'] ?? 9;
                return sa - sb;
            }
            return 0;
        });

    // Status counts
    const statusCounts = {
        ALL: myRequests.length,
        PENDING: myRequests.filter(r => (r.approvalStatus || r.status || 'PENDING') === 'PENDING').length,
        PENDING_APPROVAL: myRequests.filter(r => (r.approvalStatus || r.status) === 'PENDING_APPROVAL').length,
        APPROVED: myRequests.filter(r => (r.approvalStatus || r.status) === 'APPROVED').length,
        DENIED: myRequests.filter(r => (r.approvalStatus || r.status) === 'DENIED').length,
    };

    const getStatusBadge = (request) => {
        const status = request.approvalStatus || request.status || 'PENDING';
        const config = STATUS_BADGES[status] || { label: status, color: '#383d41', bg: '#e2e3e5', icon: '❓' };

        return (
            <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600',
                color: config.color,
                background: config.bg,
                whiteSpace: 'nowrap',
            }}>
                {config.icon} {config.label}
            </span>
        );
    };

    const getRecommendationBadge = (recommendation) => {
        if (!recommendation) return null;
        const config = RECOMMENDATION_BADGES[recommendation] || null;
        if (!config) return null;

        return (
            <span style={{
                display: 'inline-flex',
                padding: '2px 8px',
                borderRadius: '8px',
                fontSize: '11px',
                fontWeight: '600',
                color: config.color,
                background: config.bg,
            }}>
                {config.label}
            </span>
        );
    };

    // Build timeline for a request
    const getTimeline = (req) => {
        const events = [];
        if (req.createdAt) {
            events.push({ label: 'Request Created', date: req.createdAt, icon: '📝' });
        }
        if (req.approvalCreatedAt) {
            events.push({ label: 'Sent for Approval', date: req.approvalCreatedAt, icon: '📤' });
        }
        if (req.approvalLastUpdated && req.approvalLastAction) {
            const actionLabel = req.approvalLastAction === 'APPROVE' ? 'Approved' :
                               req.approvalLastAction === 'DENY' ? 'Denied' : req.approvalLastAction;
            events.push({ 
                label: `${actionLabel}${req.lastApproverAlias ? ` by ${req.lastApproverAlias}` : ''}`, 
                date: req.approvalLastUpdated, 
                icon: req.approvalLastAction === 'APPROVE' ? '✅' : req.approvalLastAction === 'DENY' ? '❌' : '🔄' 
            });
        }
        if (req.approvalCompletedAt && !req.approvalLastUpdated) {
            events.push({ label: 'Completed', date: req.approvalCompletedAt, icon: '🏁' });
        }
        return events.sort((a, b) => new Date(a.date) - new Date(b.date));
    };

    return (
        <div className="container">
            <div className="header">
                <h1>📊 My Requests</h1>
                <p>Track your access requests and view validation results</p>
            </div>

            <div className="form-body">

                {/* ═══════════════════════════════════════ */}
                {/* MY REQUESTS SECTION */}
                {/* ═══════════════════════════════════════ */}
                <div className="section">
                    <div className="section-title">📋 My Requests</div>

                    {!userAlias ? (
                        /* Alias Input */
                        <form onSubmit={handleAliasSubmit}>
                            <div className="form-group">
                                <label htmlFor="aliasInput">
                                    Enter your alias to view your requests
                                </label>
                                <div className="search-input-group">
                                    <input
                                        type="text"
                                        id="aliasInput"
                                        value={aliasInput}
                                        onChange={(e) => setAliasInput(e.target.value)}
                                        placeholder="Enter your alias (e.g., johndoe)"
                                        className="search-input"
                                        style={{ textTransform: 'lowercase' }}
                                    />
                                    <button type="submit" className="btn btn-primary search-btn">
                                        🔍 Load My Requests
                                    </button>
                                </div>
                            </div>
                        </form>
                    ) : (
                        <>
                            {/* User bar */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: '12px',
                                marginBottom: '16px',
                                padding: '12px 16px',
                                background: 'rgba(255,255,255,0.05)',
                                borderRadius: '8px',
                                border: '1px solid rgba(255,255,255,0.1)',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '20px' }}>👤</span>
                                    <div>
                                        <div style={{ fontWeight: '600', fontSize: '15px' }}>{userAlias}</div>
                                        <div style={{ fontSize: '12px', opacity: 0.6 }}>
                                            {myRequests.length} request{myRequests.length !== 1 ? 's' : ''} found
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button
                                        className="btn-text"
                                        onClick={() => fetchMyRequests(userAlias)}
                                        disabled={loadingRequests}
                                        style={{ fontSize: '13px' }}
                                    >
                                        🔄 Refresh
                                    </button>
                                    <button
                                        className="btn-text"
                                        onClick={handleChangeAlias}
                                        style={{ fontSize: '13px' }}
                                    >
                                        ✏️ Change Alias
                                    </button>
                                </div>
                            </div>

                            {/* Loading */}
                            {loadingRequests && (
                                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                    <div className="spinner" style={{ margin: '0 auto 12px' }}></div>
                                    <p style={{ opacity: 0.7 }}>Loading your requests...</p>
                                </div>
                            )}

                            {/* Error */}
                            {requestsError && (
                                <div style={{
                                    padding: '16px',
                                    background: 'rgba(220,53,69,0.1)',
                                    border: '1px solid rgba(220,53,69,0.3)',
                                    borderRadius: '8px',
                                    color: '#ff6b6b',
                                    marginBottom: '16px',
                                }}>
                                    ❌ {requestsError}
                                </div>
                            )}

                            {/* No requests */}
                            {!loadingRequests && !requestsError && myRequests.length === 0 && (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '40px 20px',
                                    opacity: 0.6,
                                }}>
                                    <div style={{ fontSize: '40px', marginBottom: '12px' }}>📭</div>
                                    <p style={{ fontSize: '15px', fontWeight: '500' }}>No requests found</p>
                                    <p style={{ fontSize: '13px', marginTop: '8px' }}>
                                        Submit a UFS access request from the home page to get started.
                                    </p>
                                </div>
                            )}

                            {/* Requests list */}
                            {!loadingRequests && myRequests.length > 0 && (
                                <>
                                    {/* Filter tabs */}
                                    <div style={{
                                        display: 'flex',
                                        gap: '6px',
                                        marginBottom: '12px',
                                        flexWrap: 'wrap',
                                    }}>
                                        {Object.entries(statusCounts).map(([status, count]) => {
                                            if (count === 0 && status !== 'ALL') return null;
                                            return (
                                                <button
                                                    key={status}
                                                    onClick={() => setFilterStatus(status)}
                                                    style={{
                                                        padding: '6px 14px',
                                                        borderRadius: '20px',
                                                        border: filterStatus === status ? '2px solid #ff9900' : '1px solid rgba(255,255,255,0.15)',
                                                        background: filterStatus === status ? 'rgba(255,153,0,0.15)' : 'rgba(255,255,255,0.05)',
                                                        color: filterStatus === status ? '#ff9900' : 'inherit',
                                                        cursor: 'pointer',
                                                        fontSize: '12px',
                                                        fontWeight: filterStatus === status ? '600' : '400',
                                                        transition: 'all 0.2s',
                                                    }}
                                                >
                                                    {status === 'ALL' ? 'All' :
                                                     status === 'PENDING_APPROVAL' ? 'Pending Approval' :
                                                     status.charAt(0) + status.slice(1).toLowerCase()}{' '}
                                                    ({count})
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* Search + Sort bar */}
                                    <div style={{
                                        display: 'flex', gap: '10px', marginBottom: '16px',
                                        alignItems: 'center', flexWrap: 'wrap'
                                    }}>
                                        <input
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder="Search by ID, team, use case..."
                                            style={{
                                                flex: 1, minWidth: '200px', padding: '8px 12px',
                                                borderRadius: '6px', border: '1px solid rgba(255,255,255,0.15)',
                                                background: 'rgba(255,255,255,0.05)', color: 'inherit',
                                                fontSize: '13px'
                                            }}
                                        />
                                        <select
                                            value={sortBy}
                                            onChange={(e) => setSortBy(e.target.value)}
                                            style={{
                                                padding: '8px 12px', borderRadius: '6px',
                                                border: '1px solid rgba(255,255,255,0.15)',
                                                background: 'rgba(255,255,255,0.05)', color: 'inherit',
                                                fontSize: '13px', cursor: 'pointer'
                                            }}
                                        >
                                            <option value="date_desc">Newest First</option>
                                            <option value="date_asc">Oldest First</option>
                                            <option value="status">By Status</option>
                                        </select>
                                    </div>

                                    {/* Request cards */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {filteredRequests.map((req) => (
                                            <div
                                                key={req.requestId}
                                                onClick={() => handleQuickView(req.requestId)}
                                                style={{
                                                    padding: '16px 20px',
                                                    background: 'rgba(255,255,255,0.03)',
                                                    border: '1px solid rgba(255,255,255,0.1)',
                                                    borderRadius: '10px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: '16px',
                                                }}
                                                onMouseOver={(e) => {
                                                    e.currentTarget.style.background = 'rgba(255,153,0,0.08)';
                                                    e.currentTarget.style.borderColor = 'rgba(255,153,0,0.3)';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                                                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                                                }}
                                            >
                                                {/* Left: Details */}
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '10px',
                                                        flexWrap: 'wrap',
                                                        marginBottom: '6px',
                                                    }}>
                                                        <span style={{
                                                            fontWeight: '700',
                                                            fontSize: '14px',
                                                            fontFamily: 'monospace',
                                                            color: '#ff9900',
                                                        }}>
                                                            {req.requestId}
                                                        </span>
                                                        {getStatusBadge(req)}
                                                        {getRecommendationBadge(req.recommendation)}
                                                    </div>

                                                    <div style={{
                                                        display: 'flex',
                                                        gap: '16px',
                                                        fontSize: '13px',
                                                        opacity: 0.7,
                                                        flexWrap: 'wrap',
                                                    }}>
                                                        {req.functionDropdown && (
                                                            <span>📂 {req.functionDropdown}</span>
                                                        )}
                                                        {(req.teamLabel || req.orgName) && (
                                                            <span>👥 {req.teamLabel || req.orgName}</span>
                                                        )}
                                                        {req.useCase && (
                                                            <span>📝 {req.useCase}</span>
                                                        )}
                                                        {req.totalUsers != null && (
                                                            <span>👤 {req.totalEligible || 0}/{req.totalUsers} eligible</span>
                                                        )}
                                                    </div>

                                                    <div style={{
                                                        fontSize: '12px',
                                                        opacity: 0.5,
                                                        marginTop: '4px',
                                                    }}>
                                                        {formatDate(req.createdAt)}
                                                        {req.approvalId && (
                                                            <span style={{ marginLeft: '12px' }}>
                                                                Approval: {req.approvalId}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Timeline */}
                                                    {getTimeline(req).length > 1 && (
                                                        <div style={{
                                                            display: 'flex', gap: '0', alignItems: 'center',
                                                            marginTop: '8px', flexWrap: 'wrap'
                                                        }}>
                                                            {getTimeline(req).map((event, idx, arr) => (
                                                                <React.Fragment key={idx}>
                                                                    <span style={{
                                                                        display: 'inline-flex', alignItems: 'center', gap: '3px',
                                                                        fontSize: '11px', opacity: 0.7,
                                                                        padding: '2px 6px', borderRadius: '4px',
                                                                        background: 'rgba(255,255,255,0.05)'
                                                                    }}>
                                                                        {event.icon} {event.label}
                                                                        <span style={{ fontSize: '10px', opacity: 0.6, marginLeft: '2px' }}>
                                                                            {formatDate(event.date).split(',')[0]}
                                                                        </span>
                                                                    </span>
                                                                    {idx < arr.length - 1 && (
                                                                        <span style={{ margin: '0 4px', opacity: 0.3, fontSize: '10px' }}>→</span>
                                                                    )}
                                                                </React.Fragment>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Right: Arrow */}
                                                <div style={{
                                                    fontSize: '20px',
                                                    opacity: 0.4,
                                                    flexShrink: 0,
                                                }}>
                                                    →
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {filteredRequests.length === 0 && (
                                        <div style={{
                                            textAlign: 'center',
                                            padding: '30px',
                                            opacity: 0.5,
                                            fontSize: '14px',
                                        }}>
                                            No requests matching this filter.
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>

                {/* ═══════════════════════════════════════ */}
                {/* MANUAL LOOKUP SECTION */}
                {/* ═══════════════════════════════════════ */}
                <div className="section">
                  <div className="section-title">🔍 Look Up by Request ID</div>
                 <form onSubmit={handleViewResults}>
                    <div className="form-group">
                        <label htmlFor="requestId">
                            Request ID
                        </label>
                        <div className="search-input-group">
                            <input
                                type="text"
                                id="requestId"
                                value={requestId}
                                onChange={(e) => setRequestId(e.target.value)}
                                placeholder="Enter Request ID (e.g., UFS-58D19105)"
                                className="search-input"
                            />
                            <button type="submit" className="btn btn-primary search-btn">
                                📊 View Results
                            </button>
                        </div>
                        <div className="hint">
                            Enter any Request ID to view its validation results directly.
                        </div>
                    </div>
                </form>

                    {/* Where to find Request ID — BELOW the form */}
                    <div className="request-id-helper">
                        <div className="request-id-helper-title">
                            💡 Where to find your Request ID
                        </div>
                        <div className="request-id-helper-grid">
                            <div className="request-id-helper-card">
                                <div className="request-id-helper-card-header">
                                    🎫 From SIM Ticket
                                </div>
                                <div className="request-id-helper-card-body">
                                    <img
                                        src="/images/sim-request-id.png"
                                        alt="Request ID location in SIM ticket"
                                        className="request-id-helper-img"
                                        onClick={(e) => window.open(e.target.src, '_blank')}
                                    />
                                    <p className="request-id-helper-caption">
                                        Find the Request ID in the <strong>Validation Check URL</strong> at the bottom of your SIM ticket.
                                    </p>
                                </div>
                            </div>
                            <div className="request-id-helper-card">
                                <div className="request-id-helper-card-header">
                                    ✅ From Approvals Page
                                </div>
                                <div className="request-id-helper-card-body">
                                    <img
                                        src="/images/approvals-request-id.png"
                                        alt="Request ID location in Approvals"
                                        className="request-id-helper-img"
                                        onClick={(e) => window.open(e.target.src, '_blank')}
                                    />
                                    <p className="request-id-helper-caption">
                                        Find the Request ID in the <strong>requestId</strong> or <strong>viewerUrl</strong> fields on the Approvals page.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ═══════════════════════════════════════ */}
                {/* RECENT SEARCHES */}
                {/* ═══════════════════════════════════════ */}
                {recentSearches.length > 0 && (
                    <div className="section">
                        <div className="section-title-row">
                            <div className="section-title">🕐 Recent Searches</div>
                            <button className="btn-text" onClick={handleClearHistory}>
                                Clear History
                            </button>
                        </div>
                        <div className="recent-searches-list">
                            {recentSearches.map((search, index) => (
                                <div
                                    key={`${search.id}-${index}`}
                                    className="recent-search-item"
                                    onClick={() => handleQuickView(search.id)}
                                >
                                    <div className="recent-search-info">
                                        <span className="recent-search-id">📄 {search.id}</span>
                                        <span className="recent-search-date">{formatDate(search.timestamp)}</span>
                                    </div>
                                    <span className="recent-search-arrow">→</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UFSValidationResults;